import { useCallback } from "react";
import { NavLink as RouterNavLink, useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilCallback, useResetRecoilState } from "recoil";
import {
  MDBNavbarItem,
  MDBDropdownLink,
} from "mdb-react-ui-kit";
import { 
  faStream,
  faGamepad,
  faSignOutAlt,
  faCog,
  faTasks,
  faStar,
  faUser,
  faUsers,
  faThumbsUp,
  faPlay,
  faFilm,
  faDownload,
  faGift,
  faFire,
  faUserEdit,
  faMobile,
  faTimesCircle,
  faChartBar,
  faWaveSquare,
  faRobot,
  faMedal,
} from "@fortawesome/free-solid-svg-icons";
import { useIsAuthenticated, useSignOut } from "react-auth-kit";
import { useDecision } from "@optimizely/react-sdk";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { useGameQuery } from "../settings/Settings.state";
import {
  SideNavContainer,
  SideNavSpacer,
  SideNavGroup,
  BuilderNav,
  NavbarBrand,
  FontAwesomeIcon,
  CloseNavButton,
  SignOutWrapper,
} from "./SideNav.styles";
import LanguagePicker from "./LanguagePicker";
import logo from "../images/builder-logo.png";
import theme from "../themes";
import useRouteChange from "../lib/useRouteChange";
import usePreviewButton from "../hooks/usePreviewButton";
import ConsoleUiTrigger from "./ConsoleUi/ConsoleUiTrigger";
import { signInState } from '../sign-in/SignIn.state'
import { removeAuthStorages } from '../lib/auth'
import { OPEN_STATES_KEYS } from "../consts/states";
import { teamsIdState, teamsTermState } from "../teams/Teams.state";
import AppUserContainer from "./AppUserContainer";
import ErrorAlert from "./ErrorAlert";
import { ErrorBoundary } from "@rollbar/react";
import withRealTimeDot from "../hoc/withRealTimeDot";
import ROUTE_PATTERNS from "../consts/routes";

const NavLink = withRealTimeDot(RouterNavLink)

const NavLinkWithState = ({ to, state, children }) => {
  const location = useLocation()
  const history = useHistory()
  const onNavigateTab = useRecoilCallback(({snapshot}) => async (pathname, state) => {
    // Get current URL params
    const parsed = queryString.parse(location.search)

    // Retrieve current open state value from recoil snapshot
    const query = await snapshot.getPromise({
      key: state,
    })

    // Append to the URL params if applicable
    if (Array.isArray(query) && query.length > 0) {
      parsed[state] = JSON.stringify(query)
    }

    // Navigate to the page with constructed URL params
    history.push({
      pathname,
      search: queryString.stringify(parsed),
    })
  }, [])
  
  return (
    <NavLink to={to} onClick={() => onNavigateTab(to, state)}>
      { children }
    </NavLink>
  )
}

const ListMenu = ({ game_id }) => {
  const { t } = useTranslation();
  const [game] = useGameQuery(game_id);
  const { user, code } = game || {}
  const [decisionGroups] = useDecision('groups')
  const [decisionAnalytics] = useDecision('analytics')
  const [decisionAdventures] = useDecision('adventures')
  const [decisionNpc] = useDecision('npc')
  const [decisionDesignations] = useDecision('designations')
  const previewBtnProps = usePreviewButton({ code })
  const resetTeamIdState = useResetRecoilState(teamsIdState)
  const resetTeamTermState = useResetRecoilState(teamsTermState)
  
  if (user?.access === "Joined") {
    return (
      <MDBDropdownLink href={`/games/slideshow/${game_id}`}>
        <FontAwesomeIcon icon={faPlay}/> <span dangerouslySetInnerHTML={{__html: t('Slideshow')}} />
      </MDBDropdownLink>
    );
  }

  return (
    <>
      <NavLink to={`/games/edit/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faCog} /> <span dangerouslySetInnerHTML={{__html: t('Settings')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLinkWithState to={`/tasks/organize/${game_id}`} state={OPEN_STATES_KEYS.CHALLENGE}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faTasks}/> <span dangerouslySetInnerHTML={{__html: t('Challenges')}} />
        </MDBDropdownLink>
      </NavLinkWithState>
      {
        decisionNpc?.enabled
          ? (
            <NavLinkWithState to={`/games/npc/${game_id}`} state={OPEN_STATES_KEYS.NPC}>
              <MDBDropdownLink tag="span">
                <FontAwesomeIcon icon={faRobot} /> <span dangerouslySetInnerHTML={{__html: t('Non Player Characters')}} />
              </MDBDropdownLink>
            </NavLinkWithState>
          )
          : null
      }
      {
        decisionAdventures?.enabled
          ? (
            <NavLink to={`/games/path/${game_id}`}>
              <MDBDropdownLink tag="span">
                <FontAwesomeIcon icon={faWaveSquare} /> <span dangerouslySetInnerHTML={{__html: t('Path')}} />
              </MDBDropdownLink>
            </NavLink>
          )
          : null
      }
      {
        decisionDesignations?.enabled
          ? (
            <NavLinkWithState to={`/games/designations/${game_id}`} state={OPEN_STATES_KEYS.DESIGNATION}>
              <MDBDropdownLink tag="span">
                <FontAwesomeIcon icon={faMedal} /> <span dangerouslySetInnerHTML={{__html: t('Designations')}} />
              </MDBDropdownLink>
            </NavLinkWithState>
          )
          : null
      }
      <NavLink to={`/tasks/library/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faStar}/> <span dangerouslySetInnerHTML={{__html: t('Library')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/teams/organize/${game_id}`} onClick={() => {
        // Need to reset the team id and term state to avoid the url params from being appended
        // This can't be done in the useRouteChange hook because we navigate to the teams page 
        // when clicking on a team chip from the groups page.
        resetTeamIdState()
        resetTeamTermState()
      }}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faUser} /> <span dangerouslySetInnerHTML={{__html: t('Teams')}} />
        </MDBDropdownLink>
      </NavLink>
      {
        decisionGroups?.enabled
          ? (
            <NavLink to={`/groups/organize/${game_id}`}>
              <MDBDropdownLink tag="span">
                <FontAwesomeIcon icon={faUsers} /> <span dangerouslySetInnerHTML={{__html: 'Groups'}} />
              </MDBDropdownLink>
            </NavLink>
          )
          : null
      }
      {
        decisionAnalytics?.enabled
          ? (
            <NavLink to={`/games/analytics/${game_id}`}>
              <MDBDropdownLink tag="span">
                <FontAwesomeIcon icon={faChartBar} /> <span dangerouslySetInnerHTML={{__html: t('Analytics')}} />
              </MDBDropdownLink>
            </NavLink>
          )
          : null
      }
      <NavLink to={`/answers/monitor/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faThumbsUp} /> <span dangerouslySetInnerHTML={{__html: t('Monitor')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/slideshow/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faPlay} /> <span dangerouslySetInnerHTML={{__html: t('Slideshow')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/reels/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faFilm} /> <span dangerouslySetInnerHTML={{__html: t('Reels')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/exports/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faDownload} /> <span dangerouslySetInnerHTML={{__html: t('Exports')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/rewards/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faGift} /> <span dangerouslySetInnerHTML={{__html: t('Rewards')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/personalizations/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faUserEdit} /> <span dangerouslySetInnerHTML={{__html: t('Personalizations')}} />
        </MDBDropdownLink>
      </NavLink>
      <NavLink to={`/games/grey/${game_id}`}>
        <MDBDropdownLink tag="span">
          <FontAwesomeIcon icon={faFire} /> <span dangerouslySetInnerHTML={{__html: t('Branding')}} />
        </MDBDropdownLink>
      </NavLink>
      <MDBDropdownLink
        {...previewBtnProps}
      >
        <FontAwesomeIcon icon={faMobile} /> <span dangerouslySetInnerHTML={{__html: t('Preview')}} />
      </MDBDropdownLink>
    </>
  );
};


const RenderIfAuth = ({ children, isInAuthenticatedPage }) => {
  const isAuthenticated = useIsAuthenticated();
  return isInAuthenticatedPage && isAuthenticated()
    ? (
      <AppUserContainer>
        {children}
      </AppUserContainer>
    )
    : null
}

const SideNav = () => {
  const { t } = useTranslation();
  const history = useLocation();
  const [, setSignIn] = useRecoilState(signInState);
  const signOut = useSignOut();
  const [, , , game_id] = history.pathname.split("/");
  const publicPaths = [
    ROUTE_PATTERNS.SIGN_IN,
    ROUTE_PATTERNS.SIGN_UP,
    ROUTE_PATTERNS.RESET_PASSWORD,
    ROUTE_PATTERNS.MAINTENANCE,
    ROUTE_PATTERNS.SERVER_ERROR,
    ROUTE_PATTERNS.PRIVACY,
    ROUTE_PATTERNS.TERMS,
  ];

  // Check if user is in authenticated pages (private routes).
  const isInAuthenticatedPage = !publicPaths.includes(history?.pathname)
  
  const showBuilderSections =
    isInAuthenticatedPage &&
    !!game_id &&
    game_id !== "new";

  const hideNavbar = () => {
    document.body.classList.remove('navbar-active')
  }

  const onRouteChange = () => {
    hideNavbar()
  }

  useRouteChange({ onRouteChange })

  const handleSignOut = useCallback(() => {
    signOut();
    setSignIn(null);
    removeAuthStorages(true);
    window.dispatchEvent(new StorageEvent("auth-storage"));
  }, [signOut, setSignIn]);

  return (
    <>
      <SideNavSpacer />
      <SideNavContainer data-testid={isInAuthenticatedPage ? 'sidenav--loggedin' : 'sidenav--not-loggedin'}>
        <SideNavGroup>
          <NavbarBrand tag="div">
            <a href="/">
              <img src={logo} alt="Birthday Legends" />
            </a>
            <CloseNavButton color="link" onClick={hideNavbar}>
              <FontAwesomeIcon icon={faTimesCircle} />
            </CloseNavButton>
          </NavbarBrand>
          <BuilderNav
            data-test="navbar-nav"
            className="navbar-nav justify-content-end"
          >
            <MDBNavbarItem>
              <RenderIfAuth isInAuthenticatedPage={isInAuthenticatedPage}>
                <NavLink to="/dashboard">
                  <MDBDropdownLink 
                    tag="span"
                  >
                    <FontAwesomeIcon icon={faStream}/> <span dangerouslySetInnerHTML={{__html: t('Dashboard')}} />
                  </MDBDropdownLink>
                </NavLink>
                <ErrorBoundary fallbackUI={(error) => {
                  const errorObject = error?.error || error
                  return <ErrorAlert error={errorObject} />
                }}>
                  {showBuilderSections && (
                    <ListMenu game_id={game_id} />
                  )}
                </ErrorBoundary>
              </RenderIfAuth>
              <MDBDropdownLink href={theme.playerUrl} target="_blank">
                <FontAwesomeIcon icon={faGamepad}/> <span dangerouslySetInnerHTML={{__html: t('Play')}} />
              </MDBDropdownLink>
            </MDBNavbarItem>
          </BuilderNav>
        </SideNavGroup>
        <RenderIfAuth isInAuthenticatedPage={isInAuthenticatedPage}>
          <SignOutWrapper>
            <MDBDropdownLink href="#" onClick={handleSignOut}>
              <FontAwesomeIcon icon={faSignOutAlt}/> <span dangerouslySetInnerHTML={{__html: t('Sign out')}} />
            </MDBDropdownLink>
            <ConsoleUiTrigger />
            <LanguagePicker />
          </SignOutWrapper>
        </RenderIfAuth>
      </SideNavContainer>
    </>
  );
};

export default SideNav;
